import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import { FiChevronRight } from "react-icons/fi";
import DefaultLocation from "../../assets/images/france.jpg";

/* ---------------- Skeleton Loader ---------------- */
const LocationSkeleton = () => (
  <div className="flex flex-col items-center gap-3 animate-pulse">
    <div className="w-[140px] h-[200px] rounded-full bg-gray-200"></div>
    <div className="h-4 w-24 bg-gray-200 rounded"></div>
    <div className="h-3 w-20 bg-gray-200 rounded"></div>
  </div>
);

/* ---------------- Main Component ---------------- */
export default function ExploreYourLocation({
  locations = [],
  isLoading = false,
  onLocationClick,
}) {
  const swiperRef = useRef(null);

  const handleNext = () => {
    swiperRef.current?.slideNext();
  };

  return (
    <div className="container mx-auto px-2 lg:px-12 py-20 relative">
      <h2 className="text-center text-[#000000] font-bold text-2xl md:text-3xl">
        Explore By Location
      </h2>
      <p className="text-center text-[16px] md:text-lg text-[#242424] pt-1 md:pt-3">
        Discover top destinations and experiences near you
      </p>
      <div className="mt-3 h-1 w-[40%] md:w-[20%] lg:w-[11%] bg-[#1FADC6] mx-auto"></div>

      <div className="mt-12 relative">
        {isLoading ? (
          <div className="flex justify-center gap-8">
            {[...Array(6)].map((_, i) => (
              <LocationSkeleton key={i} />
            ))}
          </div>
        ) : locations.length > 0 ? (
          <>
            <Swiper
              modules={[Pagination, Navigation]}
              spaceBetween={24}
              slidesPerView={2.2}
              pagination={{ clickable: true }}
              loop={locations.length > 6}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              breakpoints={{
                375: { slidesPerView: 2.5 },
                640: { slidesPerView: 3.5 },
                768: { slidesPerView: 4.5 },
                1024: { slidesPerView: 6 },
                1280: { slidesPerView: 7 },
              }}
              className="top-exp-slider pb-[45px]"
            >
              {locations.map((l) => (
                <SwiperSlide key={l.id}>
                  <div
                    onClick={() => onLocationClick?.(l.id)}
                    className="flex flex-col items-center gap-3 cursor-pointer group"
                  >
                    <img
                      src={l.imageUrl || DefaultLocation}
                      alt={l.name}
                      className="w-[140px] h-[200px] rounded-full object-cover shadow-md group-hover:shadow-lg transition"
                      onError={(e) => {
                        e.target.src = DefaultLocation;
                      }}
                    />
                    <span className="text-gray-700 font-medium text-center">
                      {l?.label}
                    </span>
                    <span className="text-gray-400 font-medium text-[12px]">
                      Experiences coming soon
                    </span>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Custom Right Arrow */}
            {locations.length > 6 && (
              <button
                onClick={handleNext}
                className="
                  absolute right-0 top-1/2 -translate-y-1/2 -translate-x-4
                  z-10
                  h-12 w-12
                  flex items-center justify-center
                  bg-white
                  border border-[#E5E7EB]
                  rounded-full
                  shadow-lg
                  hover:bg-gray-50
                  hover:shadow-xl
                  transition-all
                  duration-300
                  group
                "
                aria-label="Next locations"
              >
                <FiChevronRight
                  size={24}
                  className="text-gray-700 group-hover:text-[#1FADC6]"
                />
              </button>
            )}
          </>
        ) : (
          <p className="text-gray-500 text-center">No locations available</p>
        )}
      </div>
    </div>
  );
}
